import { useRef, useState } from "react";
import { ChevronDown, Calendar as CalendarIcon } from "@/components/icons";
import { es } from "date-fns/locale";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { Calendar } from "./ui/calendar";

/**
 * Frosted DatePicker with KEYBOARD typing support.
 *
 * - value: ISO "YYYY-MM-DD" or ""
 * - onChange(iso): called with "YYYY-MM-DD" or ""
 * - User can type freely in dd/mm/yyyy format; on blur the value is parsed and committed.
 * - Calendar icon opens shadcn Calendar inside a Popover (locale=es).
 *
 * @param {object} props
 * @param {string} props.label
 * @param {string} props.value     ISO date "YYYY-MM-DD"
 * @param {(iso:string) => void} props.onChange
 * @param {string} [props.placeholder]
 * @param {string} [props.testid]
 * @param {number} [props.fromYear]
 * @param {number} [props.toYear]
 */
export default function DatePicker({
  label,
  value,
  onChange,
  placeholder = "dd/mm/aaaa",
  testid = "datepicker",
  fromYear = 1920,
  toYear = new Date().getFullYear() + 1,
}) {
  const [open, setOpen] = useState(false);
  // Internal "draft" while typing. When `null`, we display props value.
  const [draft, setDraft] = useState(null);
  const inputRef = useRef(null);

  const displayed = draft !== null ? draft : isoToDdmmyyyy(value);

  const commitText = (raw) => {
    const iso = parseDdmmyyyy(raw);
    if (iso) {
      onChange(iso);
      setDraft(null);
    } else if (!raw.trim()) {
      onChange("");
      setDraft(null);
    } else {
      // invalid — revert to last good value
      setDraft(null);
    }
  };

  const selectedDate = value ? new Date(`${value}T00:00:00`) : undefined;

  return (
    <label className="block" data-testid={testid}>
      {label && (
        <div className="text-xs text-black/60 font-bold uppercase tracking-wider mb-1">
          {label}
        </div>
      )}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          value={displayed}
          onChange={(e) => setDraft(autoFormat(e.target.value))}
          onBlur={(e) => commitText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitText(e.target.value);
              inputRef.current?.blur();
            }
          }}
          placeholder={placeholder}
          className="w-full px-4 py-2.5 pr-11 rounded-xl glass-input outline-none"
          data-testid={`${testid}-input`}
          autoComplete="off"
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid={`${testid}-trigger`}
              title="Abrir calendario"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-lg hover:bg-black/5 text-black/60 flex items-center justify-center transition"
            >
              <CalendarIcon className="w-4 h-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            className="w-auto p-0 glass-modal rounded-2xl border-0 shadow-lg"
            data-testid={`${testid}-content`}
          >
            <Calendar
              mode="single"
              locale={es}
              selected={selectedDate}
              defaultMonth={selectedDate}
              onSelect={(d) => {
                if (d) {
                  const iso = toLocalISO(d);
                  onChange(iso);
                  setDraft(null);
                } else {
                  onChange("");
                  setDraft(null);
                }
                setOpen(false);
              }}
              captionLayout="dropdown-buttons"
              fromYear={fromYear}
              toYear={toYear}
              classNames={{
                caption: "flex justify-center pt-1 relative items-center mb-2",
                caption_dropdowns: "flex gap-2 items-center",
                caption_label: "hidden",
                vhidden: "hidden",
                dropdown:
                  "px-2 py-1 rounded-md bg-white border border-[#E5E7EB] text-sm font-medium outline-none cursor-pointer",
                dropdown_month: "relative",
                dropdown_year: "relative",
              }}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>
    </label>
  );
}

// ============================================================================
// Date string helpers
// ============================================================================

/** "YYYY-MM-DD" -> "DD/MM/YYYY" */
function isoToDdmmyyyy(iso) {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

/** "DD/MM/YYYY" (or D/M/YY) -> "YYYY-MM-DD" or null on bad input */
function parseDdmmyyyy(raw) {
  if (!raw) return null;
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/.exec(raw.trim());
  if (!m) return null;
  let d = parseInt(m[1], 10);
  let mo = parseInt(m[2], 10);
  let y = parseInt(m[3], 10);
  if (y < 100) y = y > 50 ? 1900 + y : 2000 + y;
  if (mo < 1 || mo > 12) return null;
  if (d < 1 || d > 31) return null;
  // Validate by round-tripping through Date
  const test = new Date(y, mo - 1, d);
  if (
    test.getFullYear() !== y ||
    test.getMonth() !== mo - 1 ||
    test.getDate() !== d
  )
    return null;
  return `${y}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/** Local-timezone ISO YYYY-MM-DD (no UTC drift) */
function toLocalISO(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Auto-format while typing: insert "/" after dd and mm. */
function autoFormat(raw) {
  // Allow user to delete normally; only auto-insert if the user is typing forward digits.
  const digits = raw.replace(/[^\d/]/g, "").replace(/\/+/g, "/");
  if (/^\d{3,}$/.test(digits) && !digits.includes("/")) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  }
  if (/^\d{2}\/\d{3,}$/.test(digits)) {
    const [d, rest] = digits.split("/");
    return `${d}/${rest.slice(0, 2)}/${rest.slice(2)}`;
  }
  return digits.slice(0, 10);
}

// Also export helpers for other components
export { isoToDdmmyyyy, parseDdmmyyyy, toLocalISO };

// Re-export so existing chrome (no label, defaults) still works
export { ChevronDown };
